import {
  AssetType,
  BorrowerOnboardingDetails,
  getLoanApplicationsByLoanId,
  getLoanById,
  Kyc,
  OnboardingFulfilledBy,
  postSendEmailForNewVideoId,
  VerificationStatus,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { NIE_DATA_REQUIRED_LENDER_NAMES } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/constants/nieDataRequiredLenderNames';
import { DocumentIdAction } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/DocumentIdSection';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { Text } from '@/components/shared/components/Text';
import { APP_ROLES } from '@/config/config';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { useUser } from '@/hooks/useUser';

interface useKYCDocumentIdProps {
  loanId: string;
  toggleDialog: () => void;
  setOpened: (boolean) => void;
  onboardingDetails: BorrowerOnboardingDetails;
  kyc?: Kyc[];
  editModeAllowed?: boolean;
}

export const useKYCDocumentId = ({
  loanId,
  toggleDialog,
  setOpened,
  onboardingDetails,
  kyc,
  editModeAllowed,
}: useKYCDocumentIdProps) => {
  const { data: loan } = useCache(getLoanById, { loanId });
  const { data: loanApplications } = useCache(getLoanApplicationsByLoanId, { loanId });
  const { request, loading } = useRequest();
  const { userEntity } = useUser();

  const [dialogAction, setDialogAction] = useState<DocumentIdAction | undefined>(undefined);

  const nieDataRequired =
    (loanApplications?.some(
      (loanApplication) =>
        loanApplication?.lenderName != null && NIE_DATA_REQUIRED_LENDER_NAMES.includes(loanApplication.lenderName),
    ) &&
      loan?.assetReference[0].type === AssetType.AUTO) ??
    false;

  const toggleEditDialog = () => {
    setDialogAction(DocumentIdAction.EDIT_DIALOG);
    toggleDialog();
  };

  const onSubmitKycRepeatEmail = async () => {
    if (!loan) return;
    try {
      const requestPayload: PostSendEmailForNewVideoId = {
        externalId: loan.id,
        onboardingDetailsId: onboardingDetails.id,
      };
      await request(postSendEmailForNewVideoId, requestPayload);
    } catch (error) {
      throw new Error();
    }
    setOpened(false);
    setDialogAction(undefined);
  };

  const toggleEditModeDialog =
    editModeAllowed && onboardingDetails.userDetails?.documentId ? toggleEditDialog : undefined;

  const isUserRoleCpcBackOffice = userEntity?.role === APP_ROLES.CPC_BACK_OFFICE;

  const currentKycVerificationStatus = kyc && kyc.length > 0 ? kyc[0].verification?.status : undefined;

  const baseConditionsFulfilled =
    editModeAllowed &&
    onboardingDetails.userDetails?.documentId &&
    onboardingDetails.fulfilledBy !== OnboardingFulfilledBy.MERCHANT_POS;

  const enableSendRepeatKycEmail = baseConditionsFulfilled
    ? isUserRoleCpcBackOffice
      ? !!currentKycVerificationStatus && currentKycVerificationStatus === VerificationStatus.REJECTED
      : true
    : false;

  const documentIdActionsOptions: ActionsOption[] = [
    ...(toggleEditModeDialog
      ? [
          {
            display: <Text tId={'EDIT_INFORMATION'} />,
            onClick: toggleEditModeDialog,
            iconName: 'edit',
          },
        ]
      : []),
    {
      display: <Text tId={'SEND_REPEAT_KYC_EMAIL'} />,
      onClick: () => {
        setOpened(true);
        setDialogAction(DocumentIdAction.SEND_REPEAT_KYC_EMAIL);
      },
      iconName: 'email',
      disabled: !enableSendRepeatKycEmail,
    },
    ...(kyc && kyc.length > 0
      ? [
          {
            display: <Text tId={'SHOW_KYC_DATA'} />,
            onClick: () => {
              setOpened(true);
              setDialogAction(DocumentIdAction.CHECK_KYC_DATA);
            },
            iconName: 'fingerprint',
          },
        ]
      : []),
  ];

  return {
    dialogAction,
    setDialogAction,
    toggleEditDialog,
    onSubmitKycRepeatEmail,
    documentIdActionsOptions,
    toggleEditModeDialog,
    loan,
    nieDataRequired,
    loading,
  };
};

export interface PostSendEmailForNewVideoId {
  externalId: string;
  onboardingDetailsId: string;
}
